import { Logs } from './Logs.js';
import { Notification } from './Notification.js';
import { Storage } from './Storage.js';

export class ApiClient {
    static errorIcon = 'api-error-red.svg';
    static tokenRefreshPromises = new WeakMap();

    constructor(config, app) {
        this.config = config || {};
        this.app = app;

        Object.assign(this, this.config);

        this.timeout = this.normalizeTimeout(this.config.timeout);
    }

    normalizeTimeout(timeout) {
        const value = Number(timeout);

        if (Number.isFinite(value) && value > 0) {
            return value;
        }

        return 360000;
    }

    async query(method, endpoint, data, before, success, progress, options = {}) {
        const app = this.app;
        const requestMethod = method.toUpperCase();
        const requestData = data;

        this.lastError = null;

        new Logs("message", ['API QUERY']);
        new Logs("message", [method, endpoint, this.sanitizeForLog(data)]);

        if (before) before();

        if (this.shouldUseUploadProgress(requestMethod, progress)) {
            return this.queryWithUploadProgress(method, endpoint, requestData, success, progress, options);
        }

        if (this.shouldUseDownloadProgress(requestMethod, progress)) {
            return this.queryWithDownloadProgress(method, endpoint, requestData, success, progress, options);
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), this.timeout);

        let responseData;

        try {
            const url = this.buildUrl(method, endpoint, requestData);
            const requestOptions = this.buildOptions(requestMethod, requestData, controller);

            new Logs("message", ['End-point', url]);
            new Logs("message", [`Send with ${requestMethod} Method`]);
            if (requestMethod === 'POST') new Logs("message", ['Data sent', this.sanitizeForLog(requestData)]);

            const response = await fetch(url, requestOptions);
            responseData = await this.parseResponse(response);

            app.offline = false;

            if (!this.isSuccessfulApiResponse(response.status, responseData)) {
                const errorContext = {
                    method: requestMethod,
                    endpoint,
                    url,
                    requestData,
                    silentErrors: this.shouldSilenceErrors(requestData),
                    errorCallback: this.getErrorCallback(options)
                };
                const error = this.normalizeApiError(response.status, responseData, errorContext);

                if (await this.refreshAccessTokenAndReplay(error, {
                    method,
                    endpoint,
                    data: requestData,
                    success,
                    progress,
                    options
                })) {
                    return true;
                }

                this.handleApiErrorObject(error, errorContext);
                return false;
            }

            if (progress) progress(100);
        } catch (error) {
            this.handleRequestError(error, method, endpoint, requestData, before, success, progress, options);
            return false;
        } finally {
            window.clearTimeout(timeoutId);
        }

        if (success) success(responseData);
        return true;
    }

    shouldUseUploadProgress(method, progress) {
        return method === 'POST' && typeof progress === 'function';
    }

    shouldUseDownloadProgress(method, progress) {
        return method === 'GET' && typeof progress === 'function';
    }

    queryWithUploadProgress(method, endpoint, data, success, progress, options = {}) {
        return new Promise((resolve) => {
            const requestMethod = method.toUpperCase();
            const requestData = data;
            const url = this.buildUrl(method, endpoint, requestData);
            const body = this.buildUploadBody(requestData);
            const httpRequest = new XMLHttpRequest();

            new Logs("message", ['End-point', url]);
            new Logs("message", [`Send with ${requestMethod} Method`]);
            new Logs("message", ['Data sent', this.sanitizeForLog(requestData)]);

            httpRequest.timeout = this.timeout;
            httpRequest.responseType = 'text';

            httpRequest.upload.addEventListener('progress', (evt) => {
                if (!evt.lengthComputable) {
                    return;
                }

                progress((evt.loaded / evt.total) * 100);
            }, false);

            httpRequest.onload = () => {
                this.handleXhrResponse(httpRequest, success, progress, {
                    method: requestMethod,
                    originalMethod: method,
                    endpoint,
                    data: requestData,
                    url,
                    options
                }).then(resolve);
            };

            httpRequest.onerror = () => {
                this.handleNetworkError(method, endpoint, requestData, null, success, progress, new Error('XMLHttpRequest upload failed'), options);
                resolve(false);
            };

            httpRequest.ontimeout = () => {
                this.handleTimeout(method, endpoint, requestData, null, success, progress, options);
                resolve(false);
            };

            httpRequest.open(requestMethod, url, true);
            httpRequest.setRequestHeader('Accept', 'application/json');
            this.applyAuthorizationHeader((header, value) => httpRequest.setRequestHeader(header, value), requestData);
            httpRequest.send(body);
        });
    }

    queryWithDownloadProgress(method, endpoint, data, success, progress, options = {}) {
        return new Promise((resolve) => {
            const requestMethod = method.toUpperCase();
            const requestData = data;
            const url = this.buildUrl(method, endpoint, requestData);
            const httpRequest = new XMLHttpRequest();

            new Logs("message", ['End-point', url]);
            new Logs("message", [`Send with ${requestMethod} Method`]);

            httpRequest.timeout = this.timeout;
            httpRequest.responseType = 'text';

            httpRequest.addEventListener('progress', (evt) => {
                if (!evt.lengthComputable) {
                    return;
                }

                progress((evt.loaded / evt.total) * 100);
            }, false);

            httpRequest.onload = () => {
                const handled = this.handleXhrResponse(httpRequest, success, progress, {
                    method: requestMethod,
                    originalMethod: method,
                    endpoint,
                    data: requestData,
                    url,
                    options
                });
                handled.then(resolve);
            };

            httpRequest.onerror = () => {
                this.handleNetworkError(method, endpoint, requestData, null, success, progress, new Error('XMLHttpRequest download failed'), options);
                resolve(false);
            };

            httpRequest.ontimeout = () => {
                this.handleTimeout(method, endpoint, requestData, null, success, progress, options);
                resolve(false);
            };

            httpRequest.open(requestMethod, url, true);
            httpRequest.setRequestHeader('Accept', 'application/json');
            this.applyAuthorizationHeader((header, value) => httpRequest.setRequestHeader(header, value), requestData);
            httpRequest.send();
        });
    }

    async handleXhrResponse(httpRequest, success, progress, context = {}) {
        const responseData = this.parseResponseText(httpRequest.responseText);

        if (responseData === false) {
            this.callApiErrorCallback({
                type: "invalid_json",
                httpStatus: httpRequest.status,
                apiCode: null,
                message: "Réponse JSON invalide.",
                response: httpRequest.responseText,
                request: context
            }, {
                errorCallback: this.getErrorCallback(context.options)
            });
            return false;
        }

        this.app.offline = false;

        if (!this.isSuccessfulApiResponse(httpRequest.status, responseData)) {
            const errorContext = {
                method: context.method || null,
                endpoint: context.endpoint || httpRequest.responseURL,
                url: context.url || httpRequest.responseURL,
                requestData: context.data || null,
                silentErrors: this.shouldSilenceErrors(context.data || null),
                errorCallback: this.getErrorCallback(context.options)
            };
            const error = this.normalizeApiError(httpRequest.status, responseData, errorContext);

            if (await this.refreshAccessTokenAndReplay(error, {
                method: context.originalMethod || context.method,
                endpoint: context.endpoint || httpRequest.responseURL,
                data: context.data || null,
                success,
                progress,
                options: context.options || {}
            })) {
                return true;
            }

            this.handleApiErrorObject(error, errorContext);
            return false;
        }

        if (progress) progress(100);
        if (success) success(responseData);
        return true;
    }

    buildUrl(method, endpoint, data) {
        let requestEndpoint = endpoint;

        if (!requestEndpoint.startsWith('/')) {
            requestEndpoint = `/${requestEndpoint}`;
        }

        if (method.toLowerCase() === 'get') {
            const queryString = this.buildGetQueryString(data);

            if (queryString) {
                requestEndpoint = this.appendQueryString(requestEndpoint, queryString);
            }
        }

        requestEndpoint = this.appendDeviceIdQueryString(requestEndpoint, data);
        return this.url + encodeURI(requestEndpoint).replaceAll("%25", "%");
    }

    appendQueryString(endpoint, queryString) {
        if (!queryString) {
            return endpoint;
        }

        const separator = endpoint.includes('?') ? '&' : '?';

        return `${endpoint}${separator}${queryString}`;
    }

    appendDeviceIdQueryString(endpoint, data = null) {
        const idDevice = this.getRequestDeviceId(data);

        if (!idDevice) {
            return endpoint;
        }

        const params = new URLSearchParams(endpoint.split('?')[1] || "");

        if (params.has("id_device")) {
            return endpoint;
        }

        return this.appendQueryString(endpoint, `id_device=${encodeURIComponent(idDevice)}`);
    }

    getRequestDeviceId(data = null) {
        if (data instanceof FormData && data.has("id_device")) {
            return data.get("id_device");
        }

        if (data instanceof URLSearchParams && data.has("id_device")) {
            return data.get("id_device");
        }

        if (data && typeof data === "object" && data.id_device) {
            return data.id_device;
        }

        return this.app?.id_device || this.app?.idDevice || null;
    }

    buildGetQueryString(data) {
        if (!data) {
            return "";
        }

        if (typeof data === "string") {
            return data;
        }

        if (data instanceof URLSearchParams) {
            const params = new URLSearchParams(data);
            params.delete("_authToken");
            return params.toString();
        }

        if (data instanceof FormData) {
            const params = new URLSearchParams();
            data.forEach((value, key) => {
                if (key !== "_authToken") params.append(key, value);
            });

            return params.toString();
        }

        if (typeof data === "object") {
            const params = new URLSearchParams();
            Object.entries(this.cleanRequestData(data)).forEach(([key, value]) => {
                if (value === undefined || value === null) {
                    return;
                }

                params.append(key, typeof value === "object" ? JSON.stringify(value) : value);
            });

            return params.toString();
        }

        return String(data);
    }

    buildOptions(method, data, controller, authOptions = {}) {
        const options = {
            method,
            signal: controller.signal,
            headers: {
                Accept: 'application/json'
            }
        };

        this.applyAuthorizationHeader((header, value) => {
            options.headers[header] = value;
        }, data, authOptions);

        if (method === 'POST') {
        if (data instanceof FormData) {
            const formData = new FormData();
            data.forEach((value, key) => {
                if (!['_authToken', '_silentErrors'].includes(key)) formData.append(key, value);
            });
            options.body = formData;
            } else if (data) {
                options.headers['Content-Type'] = 'application/json';
                options.body = JSON.stringify(this.cleanRequestData(data));
            } else {
                options.body = new FormData();
            }
        }

        return options;
    }

    applyAuthorizationHeader(setHeader, data = null, options = {}) {
        const token = this.getAuthorizationToken(data, options);
        new Logs("message", ["API AUTHORIZATION TOKEN", token ? "[present]" : "[absent]"]);

        if (!token) {
            return false;
        }

        setHeader('Authorization', `Bearer ${token}`);
        return true;
    }

    getAuthorizationToken(data = null, options = {}) {
        if (this.app?.user && !options.preferExplicitToken) {
            return this.app.accessToken || this.app.token || null;
        }

        if (data instanceof FormData && data.has('_authToken')) {
            return data.get('_authToken');
        }

        if (data && typeof data === 'object' && data._authToken) {
            return data._authToken;
        }

        return this.app?.accessToken || this.app?.token || this.publicToken || null;
    }

    cleanRequestData(data) {
        if (!data || typeof data !== 'object' || data instanceof FormData) {
            return data;
        }

        const cleanData = { ...data };
        delete cleanData._authToken;
        delete cleanData._silentErrors;

        return cleanData;
    }

    shouldSilenceErrors(data = null) {
        if (data instanceof FormData && data.has('_silentErrors')) {
            const value = data.get('_silentErrors');
            return value === true || String(value) === 'true';
        }

        return !!(data && typeof data === 'object' && data._silentErrors);
    }

    async refreshAccessTokenAndReplay(error, request) {
        new Logs("message", ["API REFRESH ACCESS TOKEN AND REPLAY"]);

        if (!this.shouldRefreshAccessToken(error, request.options)) {
            return false;
        }

        try {
            this.notifyDebugTokenRefresh("Rafraîchissement de session", "L'accessToken est expiré, une demande de renouvellement est lancée.");
            const response = await this.refreshAccessTokenFromStorage();
            const accessToken = await this.applyRefreshedAccessToken(response);

            if (!accessToken) {
                return false;
            }

            this.notifyDebugTokenRefresh("Session renouvelée", "La requête initiale est relancée avec le nouvel accessToken.");

            // Replay the original request once with the updated Budoevents token. If the original
            // call forced `_authToken`, replace it so the retry cannot reuse the expired token.
            return this.query(
                request.method,
                request.endpoint,
                this.replaceRequestAuthToken(request.data, accessToken),
                null,
                request.success,
                request.progress,
                {
                    ...request.options,
                    tokenRefreshRetried: true
                }
            );
        } catch (refreshError) {
            new Logs("error", ["API REFRESH ACCESS TOKEN FAILED", refreshError]);
            this.notifyDebugTokenRefresh("Session non renouvelée", refreshError?.message || "Le refreshToken n'a pas permis de renouveler la session.");
            return false;
        }
    }

    shouldRefreshAccessToken(error, options = {}) {
        new Logs("message", ["API SHOULD REFRESH ACCESS TOKEN"]);

        if (options.skipTokenRefresh || options.tokenRefreshRetried) {
            return false;
        }

        return error?.type === "need_refresh";
    }

    async refreshAccessTokenFromStorage() {
        new Logs("message", ["API REFRESH ACCESS TOKEN FROM STORAGE"]);

        const refreshOwner = this.app && typeof this.app === "object" ? this.app : this;
        const pendingRefresh = ApiClient.tokenRefreshPromises.get(refreshOwner);

        if (pendingRefresh) {
            return pendingRefresh;
        }

        const refreshPromise = (async () => {
            const refreshToken = await Storage.getStorageToken();

            if (!refreshToken) {
                throw new Error("Aucun refreshToken stocké n'est disponible.");
            }

            // The refresh endpoint authenticates with the refreshToken, not with the expired accessToken.
            return this.queryRefreshEndpoint(refreshToken);
        })();

        ApiClient.tokenRefreshPromises.set(refreshOwner, refreshPromise);

        try {
            return await refreshPromise;
        } finally {
            if (ApiClient.tokenRefreshPromises.get(refreshOwner) === refreshPromise) {
                ApiClient.tokenRefreshPromises.delete(refreshOwner);
            }
        }
    }

    // Dedicated low-level request: it must not call query(), otherwise an expired refresh
    // could recursively trigger the normal retry machinery.
    async queryRefreshEndpoint(refreshToken) {
        new Logs("message", ["API QUERY REFRESH ENDPOINT"]);

        return this.queryWithRefreshToken("/auth/refresh", refreshToken, {
            refreshToken
        });
    }

    async querySessionCheckEndpoint(refreshToken, endpoint = "/users/check") {
        new Logs("message", ["API QUERY SESSION CHECK ENDPOINT"]);

        return this.queryWithRefreshToken(endpoint, refreshToken);
    }

    // Dedicated low-level request: it must not call query(), otherwise an invalid refresh
    // token could enter the normal access-token retry machinery.
    async queryWithRefreshToken(endpoint, refreshToken, data = {}) {
        const requestData = {
            _authToken: refreshToken,
            ...data
        };
        const url = this.buildUrl("post", endpoint, requestData);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, this.buildOptions("POST", requestData, controller, {
                preferExplicitToken: true
            }));
            const responseData = await this.parseResponse(response);

            if (!this.isSuccessfulApiResponse(response.status, responseData)) {
                const error = this.normalizeApiError(response.status, responseData, {
                    method: "POST",
                    endpoint,
                    url,
                    requestData,
                    silentErrors: true
                });
                throw new Error(error.content || error.message || "Le renouvellement de session a échoué.");
            }

            return responseData;
        } finally {
            window.clearTimeout(timeoutId);
        }
    }

    async applyRefreshedAccessToken(response) {
        new Logs("message", ["API APPLY REFRESHED ACCESS TOKEN"]);

        const accessToken = response?.accessToken || response?.result?.accessToken || response?.result || null;
        const refreshToken = response?.refreshToken || response?.result?.refreshToken || null;
        const user = response?.user || response?.result?.user || null;

        if (!accessToken || typeof accessToken !== "string") {
            new Logs("error", ["API REFRESH RESPONSE WITHOUT ACCESS TOKEN", response]);
            return false;
        }

        this.app.accessToken = accessToken;
        this.app.token = accessToken;
        await Storage.set("accessToken", accessToken);

        if (refreshToken) {
            this.app.refreshToken = refreshToken;
            await Storage.setStorageToken(refreshToken);
        }

        if (user) {
            this.app.user = user;
            await Storage.set("accountUser", user);
        }

        return accessToken;
    }

    replaceRequestAuthToken(data, accessToken) {
        new Logs("message", ["API REPLACE REQUEST AUTH TOKEN"]);

        if (!data || !accessToken) {
            return data;
        }

        if (data instanceof FormData) {
            const formData = new FormData();
            data.forEach((value, key) => formData.append(key, key === "_authToken" ? accessToken : value));
            return formData;
        }

        if (data instanceof URLSearchParams) {
            const params = new URLSearchParams(data);
            if (params.has("_authToken")) params.set("_authToken", accessToken);
            return params;
        }

        if (typeof data === "object" && data._authToken) {
            return {
                ...data,
                _authToken: accessToken
            };
        }

        return data;
    }

    // Refresh notifications are intentionally development-only; production keeps the
    // accessToken renewal silent while Logs still expose the full sequence.
    notifyDebugTokenRefresh(title, content) {
        new Logs("message", ["API NOTIFY DEBUG TOKEN REFRESH", title, content]);

        if (!this.app?.debug) {
            return false;
        }

        new Notification({
            icon: "warning.svg",
            title,
            content,
            close: true,
            class: "no-icon-filter"
        });

        return true;
    }

    buildUploadBody(data) {
        if (data instanceof FormData) {
            const formData = new FormData();
            data.forEach((value, key) => {
                if (!['_authToken', '_silentErrors'].includes(key)) formData.append(key, value);
            });
            return formData;
        }

        const formData = new FormData();

        if (data && typeof data === 'object') {
            Object.entries(this.cleanRequestData(data)).forEach(([key, value]) => formData.append(key, value));
        }

        return formData;
    }

    async parseResponse(response) {
        const text = await response.text();

        if (!text) {
            return null;
        }

        const data = this.parseResponseText(text);

        if (data === false) {
            throw new Error('Invalid JSON response');
        }

        return data;
    }

    parseResponseText(text) {
        try {
            const data = JSON.parse(text);
            new Logs("message", ['DATA RECEIVED', this.sanitizeForLog(data)]);
            return data;
        } catch (error) {
            this.handleInvalidJson(text, error);
            return false;
        }
    }

    sanitizeForLog(value) {
        const sensitiveKeys = /^(?:pass|password|accessToken|access_token|refreshToken|refresh_token|token|_authToken)$/i;

        if (value instanceof FormData) {
            const output = {};
            value.forEach((item, key) => {
                output[key] = sensitiveKeys.test(key) ? '[redacted]' : item;
            });
            return output;
        }

        if (Array.isArray(value)) {
            return value.map((item) => this.sanitizeForLog(item));
        }

        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([key, item]) => [
                key,
                sensitiveKeys.test(key) ? '[redacted]' : this.sanitizeForLog(item)
            ]));
        }

        return value;
    }

    handleInvalidJson(responseText, error) {
        new Logs("error", ['[API INVALID JSON]', {
            error,
            responseText
        }]);
        new Notification({
            icon: ApiClient.errorIcon,
            title: 'Erreur de donnees',
            content: 'Les donnees retournees par le serveur sont erronees, contactez le support technique.',
            close: true,
            wait: true
        });

        document.querySelectorAll('.ui-modal .ui-modal-loading').forEach((element) => {
            element.classList.remove('visible');
        });
    }

    handleRequestError(error, method, endpoint, data, before, success, progress, options = {}) {
        if (error.name === 'AbortError') {
            this.handleTimeout(method, endpoint, data, before, success, progress, options);
            return;
        }

        this.handleNetworkError(method, endpoint, data, before, success, progress, error, options);
    }

    handleTimeout(method, endpoint, data, before, success, progress, options = {}) {
        new Logs("error", ['[API TIMEOUT]', {
            method,
            endpoint,
            data,
            timeout: this.timeout
        }]);

        const apiError = {
            type: "timeout",
            httpStatus: 0,
            apiCode: null,
            message: "Délai de réponse dépassé.",
            request: {
                method,
                endpoint,
                requestData: data
            }
        };

        this.lastError = apiError;
        this.callApiErrorCallback(apiError, {
            errorCallback: this.getErrorCallback(options)
        });

        if (this.shouldSilenceErrors(data)) {
            return;
        }

        if (this.app.offline) {
            return;
        }

        new Notification({
            title: 'Erreur de connexion',
            content: 'Desole, le delai de reponse du serveur est depasse. Verifiez votre connexion internet et reessayez.',
            close: true,
            wait: true,
            confirm: true,
            confirm_true_text: 'Reessayer',
            confirm_true: () => {
                this.query(method, endpoint, data, before, success, progress, options);
            },
            confirm_close: true
        });

        this.app.offline = true;
    }

    handleNetworkError(method, endpoint, data, before, success, progress, error, options = {}) {
        new Logs("error", ['[API NETWORK ERROR]', {
            method,
            endpoint,
            data,
            error
        }]);

        const apiError = {
            type: "network_error",
            httpStatus: 0,
            apiCode: null,
            message: error?.message || "Erreur réseau.",
            error,
            request: {
                method,
                endpoint,
                requestData: data
            }
        };

        this.lastError = apiError;
        this.callApiErrorCallback(apiError, {
            errorCallback: this.getErrorCallback(options)
        });

        if (this.shouldSilenceErrors(data)) {
            return;
        }

        if (this.app.offline) {
            return;
        }

        new Notification({
            title: 'Erreur de connexion',
            content: 'Votre connexion semble interrompue. Verifiez votre connexion internet et reessayez.',
            close: true,
            wait: true,
            confirm: true,
            confirm_true_text: 'Reessayer',
            confirm_true: () => {
                this.query(method, endpoint, data, before, success, progress, options);
            },
            confirm_close: true
        });

        this.app.offline = true;
    }

    isApiErrorResponse(data) {
        const status = String(data?.status || '').toLowerCase();

        return !!data && typeof data === 'object' && status !== '' && !this.isApiOkResponse(data);
    }

    isApiOkResponse(data) {
        new Logs("message", ["API IS API OK RESPONSE"]);

        const status = String(data?.status || '').toLowerCase();

        return !!data && typeof data === 'object' && status === 'ok';
    }

    isSuccessfulApiResponse(httpStatus, data) {
        new Logs("message", ["API IS SUCCESSFUL API RESPONSE"]);

        return Number(httpStatus) === 200 && this.isApiOkResponse(data);
    }

    handleApiError(httpStatus, data, context = {}) {
        const error = this.normalizeApiError(httpStatus, data, context);

        this.handleApiErrorObject(error, context);
    }

    handleApiErrorObject(error, context = {}) {
        new Logs("message", ["API HANDLE API ERROR OBJECT"]);

        this.lastError = error;

        new Logs("error", ['[API ERROR]', error]);
        this.callApiErrorCallback(error, context);

        if (context.silentErrors) {
            return;
        }

        const notification = {
            title: error.title,
            content: error.content,
            icon: error.icon,
            close: true,
            class: error.className
        };

        if (error.type !== 'api_business_error') {
            notification.wait = true;
        }

        new Notification(notification);
    }

    getErrorCallback(options = {}) {
        new Logs("message", ["API GET ERROR CALLBACK"]);

        return options?.error || options?.onError || options?.onApiError || null;
    }

    callApiErrorCallback(error, context = {}) {
        new Logs("message", ["API CALL API ERROR CALLBACK", error]);

        if (typeof context.errorCallback !== "function") {
            return false;
        }

        try {
            context.errorCallback(error);
        } catch (callbackError) {
            new Logs("error", ["API ERROR CALLBACK FAILED", callbackError]);
            return false;
        }

        return true;
    }

    normalizeApiError(httpStatus, data, context = {}) {
        const status = Number(httpStatus) || 0;
        const rawApiCode = data && typeof data === 'object' ? data.code ?? null : null;
        const numericApiCode = Number(rawApiCode);
        const apiCode = rawApiCode !== null && Number.isFinite(numericApiCode)
            ? numericApiCode
            : rawApiCode;
        const type = this.getApiErrorType(status, apiCode, data);
        const fallback = this.getHttpErrorFallback(type.status);
        const message = data && data.message ? data.message : fallback.content;
        const detail = {
            type: type.key,
            httpStatus: status,
            apiCode,
            apiStatus: data?.status || null,
            message,
            info: data?.info || null,
            error: data?.error || null,
            file: data?.file || null,
            line: data?.line || null,
            response: data,
            request: context
        };

        return {
            ...detail,
            title: this.getApiErrorTitle(detail, fallback),
            content: this.getApiErrorContent(detail, fallback),
            icon: this.getApiErrorIcon(detail, fallback),
            className: this.getApiErrorClassName(detail, fallback)
        };
    }

    getApiErrorTitle(error, fallback) {
        if (error.type === 'api_business_error') {
            return 'Erreur';
        }

        return 'Erreur API';
    }

    getApiErrorIcon(error, fallback) {
        if (error.type === 'api_business_error') {
            return 'warning.svg';
        }

        return fallback.icon;
    }

    getApiErrorClassName(error, fallback) {
        if (error.type === 'api_business_error') {
            return 'no-icon-filter';
        }

        return fallback.className;
    }

    getApiErrorContent(error, fallback) {
        if (error.type === 'api_business_error') {
            return error.message || fallback.content;
        }

        return fallback.content;
    }

    getApiErrorType(status, apiCode, data) {
        const responseType = String(data?.type || data?.error_type || '').toLowerCase();

        if (Number(apiCode) === 100 || responseType === 'need_refresh' || data?.need_refresh === true) {
            return {
                key: 'need_refresh',
                status: 401
            };
        }

        if (status >= 400) {
            return {
                key: `http_${status}`,
                status
            };
        }

        if (this.isApiErrorResponse(data)) {
            if (status >= 200 && status < 300) {
                return {
                    key: 'api_business_error',
                    status: 200
                };
            }

            return {
                key: 'api_error',
                status: 500
            };
        }

        return {
            key: 'unknown_error',
            status: 500
        };
    }

    handleHttpError(status, data) {
        this.handleApiError(status, data);
    }

    getHttpErrorFallback(status) {
        const errors = {
            400: {
                title: 'Requête invalide',
                content: 'La requête envoyée au serveur est invalide.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            401: {
                title: 'Session expirée',
                content: 'Votre session a expiré ou votre authentification est invalide. Reconnectez-vous.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            403: {
                title: 'Accès refusé',
                content: 'Vous n’avez pas les droits nécessaires pour effectuer cette opération.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            404: {
                title: 'Ressource introuvable',
                content: 'La ressource demandée est introuvable.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            409: {
                title: 'Conflit',
                content: 'Cette opération entre en conflit avec des données existantes.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            422: {
                title: 'Données invalides',
                content: 'Certaines données envoyées ne peuvent pas être traitées.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            500: {
                title: 'Erreur du serveur',
                content: 'Le serveur a rencontré une erreur interne.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            501: {
                title: 'Erreur de l’API',
                content: 'Le serveur a retourné une erreur pour cette opération.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            502: {
                title: 'Service indisponible',
                content: 'Le serveur distant a retourne une erreur.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            },
            503: {
                title: 'Service indisponible',
                content: 'Le service est temporairement indisponible. Réessayez plus tard.',
                icon: ApiClient.errorIcon,
                wait: true,
                className: 'error'
            }
        };

        return errors[status] || {
            title: 'Erreur du serveur',
            content: `Le serveur a retourné une erreur ${status}.`,
            icon: ApiClient.errorIcon,
            wait: true,
            className: 'error'
        };
    }

    error501(data = null) {
        this.handleApiError(501, data);
    }
}
